/**
 * Auth client for the ora outlook plugin — Phase-1 design.
 *
 * Plan §8.8. Office.auth.getAccessToken returns 13000 in HSIP's tenant +
 * Office host combination, so we fall back to a custom OAuth flow that
 * runs inside an Office Dialog popup. WebView2 in Outlook breaks every
 * in-Office IPC mechanism we tried for the dialog → parent handoff
 * (`messageParent`, `window.opener`, BroadcastChannel, child popups) —
 * see commit history on this file for the journey. We landed on the
 * single mechanism that works reliably: server-side handoff via the
 * Product API.
 *
 * Flow:
 *   1. We generate a random sessionId.
 *   2. `Office.context.ui.displayDialogAsync` opens `/auth.html?session=…`.
 *   3. The dialog navigates to AAD (manual PKCE), exchanges the code for
 *      a token via direct fetch to AAD's token endpoint, then POSTs the
 *      token to `/api/auth/handoff/{sessionId}` on Product API.
 *   4. We poll `GET /api/auth/handoff/{sessionId}` every 1000ms; once it
 *      returns a payload, we have the token.
 *   5. We close the dialog and resolve with the cached token.
 *
 * The token is cached in memory until 60s before its `exp` claim, then
 * the dialog is opened again. Subsequent acquisitions in the same
 * session of Outlook reuse the cache silently.
 *
 * Operational prerequisite (one-time, on ProductWeb-DEV's AAD app):
 * `https://localhost:3100/auth.html` registered as an SPA-platform
 * redirect URI. No admin consent required, no scope changes beyond
 * what ora already exposes.
 *
 * Stub mode (`VITE_USE_STUB_API=1`) bypasses authClient entirely so dev
 * iteration on the UI continues to work without any AAD setup.
 *
 * Multi-pane contract:
 *   Each Outlook task pane is a separate WebView2 realm with isolated module
 *   state and localStorage. This module makes NO attempt to coordinate across
 *   panes — each pane authenticates independently. Consequences:
 *   - The `_pendingAcquisition` mutex dedupes concurrent callers within the
 *     same pane only; it will NOT dedupe across panes.
 *   - If Office blocks a second pane's `displayDialogAsync` because Pane A's
 *     dialog is still open (error 12007), the rejection is a `DialogAlreadyOpenError`
 *     which the auth cascade treats as a Tier-2 fall-through to Tier 3 (MSAL).
 *   - Pane B does NOT auto-retry after Pane A finishes; the user must
 *     re-trigger sign-in. If that UX becomes a pain point, add a
 *     BroadcastChannel "auth-finished" listener here that calls
 *     `getProductApiToken()` once Pane A's token is visible in storage.
 *   - sign-out clears only the calling pane's cache; the other pane's token
 *     remains valid until its ~1hr expiry.
 */

import { API_BASE_URL } from "../config/apiBaseUrl";

const REFRESH_BUFFER_MS = 60_000;
const FALLBACK_TOKEN_LIFETIME_MS = 50 * 60_000;
const DIALOG_PATH = "/auth.html";
const HANDOFF_POLL_MS = 1000;
const HANDOFF_TIMEOUT_MS = 5 * 60 * 1000;

// Module-load beacon. If you don't see this in the parent task pane's
// console, the bundle hasn't been refreshed since the last `npm run dev`
// rebuild — close + re-sideload the add-in.
// eslint-disable-next-line no-console
console.log("[ora-auth] parent: authClient module loaded — API calls are same-origin via the server.mjs relay");

interface CachedToken {
  token: string;
  expiresAt: number;
}

interface HandoffPayload {
  token?: string;
  expiresAt?: number;
  error?: string;
}

// localStorage key for the cached token. Versioned so a future change
// to the cached shape can invalidate stale entries by bumping the
// version. Same-origin and same-cert as the task pane, so only this
// add-in (running under https://localhost:3100 in dev) can read it.
//
// Trade-off: localStorage is readable by any script that runs in the
// same origin, so an XSS in the task pane would leak the token. We
// accept that for Phase-1 because (a) the task pane renders no
// untrusted content, (b) the JWT lifetime is ~1 hour, and (c) the
// alternative — re-prompting the user on every Automate click —
// trains them to ignore consent dialogs. Worth revisiting if the
// task pane ever loads third-party HTML.
const TOKEN_STORAGE_KEY = "ora-auth-token:v1";

function loadCachedTokenFromStorage(): CachedToken | null {
  try {
    const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedToken>;
    if (typeof parsed.token !== "string" || typeof parsed.expiresAt !== "number") {
      // Corrupted entry — drop it.
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      return null;
    }
    return { token: parsed.token, expiresAt: parsed.expiresAt };
  } catch {
    // localStorage unavailable (e.g., test env without a DOM, or a
    // host that disabled it) — fall through to in-memory only.
    return null;
  }
}

function saveCachedTokenToStorage(value: CachedToken | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    } else {
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(value));
    }
  } catch {
    // Best effort — non-fatal. The in-memory cache still works for
    // the lifetime of this module load.
  }
}

let cachedToken: CachedToken | null = loadCachedTokenFromStorage();
// Prevents concurrent callers from each opening their own dialog and generating
// independent sessionIds. All concurrent getProductApiToken() calls share one promise.
let _pendingAcquisition: Promise<string> | null = null;

/** Lets tests inject a custom token-acquisition strategy in place of the dialog. */
type TokenAcquisitionFn = () => Promise<CachedToken>;
let acquisitionStrategy: TokenAcquisitionFn | null = null;

export class AuthError extends Error {
  constructor(
    message: string,
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = "AuthError";
  }
}

export class MissingOfficeError extends Error {
  constructor() {
    super(
      "Office.context.ui.displayDialogAsync is unavailable — is the add-in " +
        "running inside Outlook?",
    );
    this.name = "MissingOfficeError";
  }
}

export class DialogCancelledError extends Error {
  constructor() {
    super("The sign-in dialog was closed before completing.");
    this.name = "DialogCancelledError";
  }
}

// Office error code 12007: "A dialog box is already opened from this host window."
// Thrown when displayDialogAsync is called while another dialog is in flight.
// The auth cascade recognises this type and falls through to Tier 3 (MSAL).
const OFFICE_DIALOG_ALREADY_OPEN_CODE = 12007;

export class DialogAlreadyOpenError extends Error {
  constructor(code: number) {
    super(`dialog unavailable: another dialog is already open (code=${code})`);
    this.name = "DialogAlreadyOpenError";
  }
}

export function _resetAuthClientForTests(): void {
  cachedToken = null;
  acquisitionStrategy = null;
  _pendingAcquisition = null;
  saveCachedTokenToStorage(null);
}

/** Test helper: directly seed the cache so tests can control expiry. */
export function _setCachedTokenForTests(value: CachedToken | null): void {
  cachedToken = value;
  saveCachedTokenToStorage(value);
}

/**
 * Test helper: replace the dialog-based acquisition with a fake. Tests use
 * this so they don't have to mock the entire Office.context.ui surface.
 */
export function _setAcquisitionStrategyForTests(
  strategy: TokenAcquisitionFn | null,
): void {
  acquisitionStrategy = strategy;
}

function tokenIsFresh(cached: CachedToken | null): boolean {
  return cached !== null && cached.expiresAt - Date.now() > REFRESH_BUFFER_MS;
}

function generateSessionId(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  // base64url, padding-stripped — 22 chars, fits the regex pattern the
  // Product API uses for validation.
  let str = "";
  for (let i = 0; i < array.length; i++) str += String.fromCharCode(array[i]);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

interface OfficeDialogOptions {
  height?: number;
  width?: number;
  displayInIframe?: boolean;
  promptBeforeOpen?: boolean;
}

interface OfficeAsyncResult<T> {
  status: number; // 0 = succeeded, 1 = failed
  value: T;
  error?: { code?: number; message?: string };
}

interface OfficeDialog {
  // Office.EventType values are string enums in current Office.js
  // (e.g., "DialogEventReceived"); older runtimes used numeric codes.
  // Accept both so we don't have to recompile when the host evolves.
  addEventHandler(
    eventType: string | number,
    handler: (arg: unknown) => void,
  ): void;
  close(): void;
}

interface OfficeUI {
  displayDialogAsync(
    url: string,
    options: OfficeDialogOptions,
    callback: (result: OfficeAsyncResult<OfficeDialog>) => void,
  ): void;
}

// Modern Office.js exposes EventType as a STRING enum
// (`"DialogEventReceived"`). The legacy numeric value (12001) is rejected
// by `dialog.addEventHandler` in current Outlook builds and the throw
// escapes the displayDialogAsync callback — silently killing the
// post-dialog poll. Pull the value from Office at runtime so we always
// match whatever the host expects, with a string-literal fallback for
// jest where Office is undefined.
const DIALOG_EVENT_RECEIVED: string | number =
  (typeof Office !== "undefined" &&
    (Office as unknown as { EventType?: { DialogEventReceived?: string | number } })
      ?.EventType?.DialogEventReceived) ||
  "DialogEventReceived";
const DIALOG_USER_CLOSED = 12006; // user dismissed without completing
const DIALOG_CROSS_ORIGIN_NAV = 12002; // dialog navigated cross-origin (expected during AAD redirect)
const ASYNC_RESULT_FAILED = 1;

/**
 * Poll the Product API handoff endpoint for the session's payload. Resolves
 * when a payload arrives. Rejects on timeout or AAD-side failure.
 */
async function pollHandoff(sessionId: string, signal: AbortSignal): Promise<HandoffPayload> {
  const url = `${API_BASE_URL}/api/auth/handoff/${encodeURIComponent(sessionId)}`;
  // eslint-disable-next-line no-console
  console.log("[ora-auth] parent: pollHandoff entered, url=", url);
  const startedAt = Date.now();
  let attempt = 0;
  while (!signal.aborted) {
    attempt += 1;
    if (Date.now() - startedAt > HANDOFF_TIMEOUT_MS) {
      throw new AuthError("Sign-in handoff timed out (5 min).");
    }
    try {
      // Log every fetch attempt so a polling-but-blocked condition is
      // visible in the console rather than silent.
      // eslint-disable-next-line no-console
      if (attempt === 1 || attempt % 4 === 0) {
        console.log("[ora-auth] parent: poll attempt", attempt, "→", url);
      }
      const response = await fetch(url, { method: "GET" });
      if (response.status === 204) {
        // Pending — wait + retry.
      } else if (response.ok) {
        const payload = (await response.json()) as HandoffPayload;
        // eslint-disable-next-line no-console
        console.log(
          "[ora-auth] parent: handoff payload received — has token?",
          !!payload.token,
          "error?",
          payload.error ?? "none",
        );
        return payload;
      } else {
        // 4xx/5xx — log + retry. The endpoint is best-effort; the dialog
        // hasn't necessarily delivered yet.
        // eslint-disable-next-line no-console
        console.warn(
          "[ora-auth] handoff poll got HTTP",
          response.status,
          "— retrying",
        );
      }
    } catch (err) {
      // Network blip — retry. Don't fail the whole flow on a transient.
      // eslint-disable-next-line no-console
      console.warn("[ora-auth] handoff poll fetch error — retrying:", err);
    }
    await new Promise((r) => setTimeout(r, HANDOFF_POLL_MS));
  }
  throw new AuthError("Sign-in handoff poll aborted.");
}

/**
 * Open `/auth.html?session=...` in an Office dialog and poll the Product
 * API handoff endpoint for the resulting token. Closes the dialog when
 * the handoff arrives (or the user cancels).
 */
async function acquireTokenViaDialog(): Promise<CachedToken> {
  // eslint-disable-next-line no-console
  console.log("[ora-auth] parent: acquireTokenViaDialog entered");
  if (acquisitionStrategy) return acquisitionStrategy();

  const ui = (typeof Office !== "undefined" ? Office?.context?.ui : undefined) as
    | OfficeUI
    | undefined;
  // eslint-disable-next-line no-console
  console.log(
    "[ora-auth] parent: Office availability —",
    "officeDefined=", typeof Office !== "undefined",
    "ui=", !!ui,
    "displayDialogAsync=", !!ui?.displayDialogAsync,
  );
  if (!ui?.displayDialogAsync) {
    throw new MissingOfficeError();
  }

  const sessionId = generateSessionId();
  const dialogUrl = `${window.location.origin}${DIALOG_PATH}?session=${encodeURIComponent(sessionId)}`;
  // eslint-disable-next-line no-console
  console.log("[ora-auth] parent: opening dialog →", dialogUrl);

  return new Promise<CachedToken>((resolve, reject) => {
    ui.displayDialogAsync(
      dialogUrl,
      { height: 60, width: 30, displayInIframe: false },
      (result) => {
        // eslint-disable-next-line no-console
        console.log(
          "[ora-auth] parent: displayDialogAsync callback fired, status=",
          result.status,
        );
        if (result.status === ASYNC_RESULT_FAILED) {
          const code = (result.error as { code?: number } | undefined)?.code ?? 0;
          if (code === OFFICE_DIALOG_ALREADY_OPEN_CODE) {
            reject(new DialogAlreadyOpenError(code));
          } else {
            reject(
              new AuthError(
                `displayDialogAsync failed: ${result.error?.message ?? "unknown error"}`,
                result.error,
              ),
            );
          }
          return;
        }
        const dialog = result.value;
        const abort = new AbortController();
        let settled = false;

        const settle = (fn: () => void): void => {
          if (settled) return;
          settled = true;
          abort.abort();
          try {
            dialog.close();
            // eslint-disable-next-line no-console
            console.log("[ora-auth] parent: dialog.close() invoked");
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("[ora-auth] parent: dialog.close() threw:", err);
          }
          fn();
        };

        // Listen for the user closing the dialog without completing.
        // We don't rely on DialogMessageReceived anymore (broken in
        // Outlook desktop's WebView2 after navigation); the handoff
        // poll is the source of truth. addEventHandler can throw if
        // Office rejects the event-type value (numeric vs string enum
        // varies across Office hosts/versions); a throw here would
        // skip the polling start, so we isolate it.
        try {
          dialog.addEventHandler(DIALOG_EVENT_RECEIVED, (arg) => {
            const evt = arg as { error?: number };
            // eslint-disable-next-line no-console
            console.log("[ora-auth] parent: DialogEventReceived code=", evt.error);
            if (evt.error === DIALOG_USER_CLOSED) {
              settle(() => reject(new DialogCancelledError()));
            } else if (evt.error === DIALOG_CROSS_ORIGIN_NAV) {
              // Expected when the dialog navigates to login.microsoftonline.com
              // for the PKCE redirect — not an error. Settling here would orphan
              // the dialog's sessionId and cause retries with a mismatched id.
              // eslint-disable-next-line no-console
              console.log("[ora-auth] parent: cross-origin nav event (AAD redirect) — ignoring, poll is authoritative");
            } else {
              // Unexpected event: log but do NOT settle. Let the poll either
              // receive the token or time out rather than abandoning sessionId.
              // eslint-disable-next-line no-console
              console.warn("[ora-auth] parent: unexpected DialogEventReceived code=", evt.error, "— keeping poll alive");
            }
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(
            "[ora-auth] parent: dialog.addEventHandler failed — proceeding without user-cancel detection. Poll will still resolve via handoff.",
            err,
          );
        }

        // Poll the Product API handoff endpoint. The dialog will POST the
        // token there once it has it.
        // eslint-disable-next-line no-console
        console.log("[ora-auth] parent: polling handoff for session", sessionId);
        pollHandoff(sessionId, abort.signal)
          .then((payload) => {
            if (payload.error) {
              settle(() =>
                reject(new AuthError(`Auth dialog failed: ${payload.error}`)),
              );
              return;
            }
            if (!payload.token) {
              settle(() => reject(new AuthError("Handoff returned no token.")));
              return;
            }
            const expiresAt =
              typeof payload.expiresAt === "number"
                ? payload.expiresAt
                : Date.now() + FALLBACK_TOKEN_LIFETIME_MS;
            settle(() => resolve({ token: payload.token!, expiresAt }));
          })
          .catch((err) => {
            if (settled) return;
            settle(() =>
              reject(
                err instanceof AuthError
                  ? err
                  : new AuthError(`Handoff poll failed: ${String(err)}`),
              ),
            );
          });
      },
    );
  });
}

/**
 * Returns a JWT scoped to the Product API audience for use in
 * `Authorization: Bearer`. Caches in memory + localStorage until ~60s
 * before expiry, so reopening the task pane (every Automate click in
 * Outlook re-instantiates the bundle) doesn't re-prompt the user as
 * long as the token is still good.
 */
export async function getProductApiToken(): Promise<string> {
  if (tokenIsFresh(cachedToken)) {
    return cachedToken!.token;
  }
  // Deduplicate concurrent callers — only one dialog should ever be open.
  // Without this, two simultaneous callers each call acquireTokenViaDialog(),
  // generating independent sessionIds; the dialog posts to only one of them,
  // leaving the other polling indefinitely.
  if (_pendingAcquisition) {
    // eslint-disable-next-line no-console
    console.log("[ora-auth] parent: getProductApiToken joining in-flight acquisition");
    return _pendingAcquisition;
  }
  _pendingAcquisition = (async () => {
    const fresh = await acquireTokenViaDialog();
    cachedToken = fresh;
    saveCachedTokenToStorage(fresh);
    return fresh.token;
  })();
  try {
    return await _pendingAcquisition;
  } finally {
    _pendingAcquisition = null;
  }
}

/**
 * Force re-authentication on the next `getProductApiToken()` call by
 * clearing both the in-memory and persisted token. Use for explicit
 * sign-out and for unrecoverable 401s where the token is logically
 * still fresh but the API has rejected it.
 */
export function signOut(): void {
  cachedToken = null;
  saveCachedTokenToStorage(null);
}
